import { useEffect, useMemo, useState } from 'react';
import { describeActivity, listAuditEvents, type AuditEventDetail } from '../../api/activity';
import { useBranch } from '../../context/BranchContext';
import { DashboardSkeleton, DemoBanner, ErrorNote, KPICard, SectionCard, Select, StatusBadge, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconClipboardCheck, IconFileText, IconShield, IconTruck } from '../../components/icons';

const RESOURCE_TYPES = ['PURCHASE_REQUEST', 'PURCHASE_ORDER', 'GOODS_RECEIPT', 'STOCK_COUNT', 'TRANSFER', 'WASTE', 'EXPIRY_LOT'];

/**
 * Audit & Compliance dashboard — genuinely read-only. No create/edit/
 * approve/post/delete affordance anywhere on this page. Every row is a
 * real audit_events entry (RLS tenant-scoped, same table the topbar
 * notification bell reads) — recent approvals, postings, receiving, and
 * stock adjustments across the tenant, filterable by branch/resource/
 * action so an auditor can narrow the trail without any mutating control.
 */
export default function AuditorDashboard() {
  const { branches } = useBranch();
  const [events, setEvents] = useState<AuditEventDetail[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [branchFilter, setBranchFilter] = useState<string>('ALL');
  const [resourceFilter, setResourceFilter] = useState<string>('ALL');
  const [actionFilter, setActionFilter] = useState<string>('ALL');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAuditEvents(200)
      .then((rows) => !cancelled && setEvents(rows))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const branchNameById = useMemo(() => Object.fromEntries(branches.map((b) => [b.branch_id, b.name])), [branches]);
  const actionOptions = useMemo(() => Array.from(new Set((events ?? []).map((e) => e.action))).sort(), [events]);

  const filtered = (events ?? []).filter(
    (e) =>
      (branchFilter === 'ALL' || e.branch_id === branchFilter) &&
      (resourceFilter === 'ALL' || e.resource_type === resourceFilter) &&
      (actionFilter === 'ALL' || e.action === actionFilter),
  );

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const approvalsToday = events ? events.filter((e) => e.action === 'APPROVE' && isToday(e.occurred_at)).length : null;
  const postingsToday = events ? events.filter((e) => e.action === 'POST' && isToday(e.occurred_at)).length : null;
  const receivingsToday = events ? events.filter((e) => e.action === 'RECEIVE' && isToday(e.occurred_at)).length : null;

  return (
    <div>
      <DemoBanner text="Mode read-only — tidak ada aksi create/edit/approve/post/delete di halaman ini. Semua baris berasal dari audit_events." />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 24 }}>
        <KPICard label="Approval hari ini" value={approvalsToday ?? '—'} icon={<IconClipboardCheck size={19} />} tone="success" />
        <KPICard label="Posting hari ini" value={postingsToday ?? '—'} icon={<IconFileText size={19} />} tone="primary" />
        <KPICard label="Penerimaan hari ini" value={receivingsToday ?? '—'} icon={<IconTruck size={19} />} tone="info" />
        <KPICard label="Total kejadian (200 terbaru)" value={events?.length ?? '—'} icon={<IconShield size={19} />} tone="neutral" />
      </div>

      <SectionCard title="Jejak Audit">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          <Select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} aria-label="Filter cabang" style={{ width: 200 }}>
            <option value="ALL">Semua cabang</option>
            {branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>
                {b.name}
              </option>
            ))}
          </Select>
          <Select value={resourceFilter} onChange={(e) => setResourceFilter(e.target.value)} aria-label="Filter jenis dokumen" style={{ width: 200 }}>
            <option value="ALL">Semua jenis dokumen</option>
            {RESOURCE_TYPES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
          <Select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} aria-label="Filter aksi" style={{ width: 160 }}>
            <option value="ALL">Semua aksi</option>
            {actionOptions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </div>

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Waktu</th>
              <th style={thStyle}>Kejadian</th>
              <th style={thStyle}>Aksi</th>
              <th style={thStyle}>Cabang</th>
              <th style={thStyle}>Referensi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td style={tdStyle} colSpan={5}>
                  Tidak ada kejadian audit yang cocok dengan filter ini.
                </td>
              </tr>
            )}
            {filtered.slice(0, 60).map((e) => (
              <tr key={e.event_id}>
                <td style={tdStyle}>{new Date(e.occurred_at).toLocaleString('id-ID')}</td>
                <td style={tdStyle}>{describeActivity(e)}</td>
                <td style={tdStyle}>
                  <StatusBadge status={e.action} />
                </td>
                <td style={tdStyle}>{e.branch_id ? (branchNameById[e.branch_id] ?? '—') : '—'}</td>
                <td style={tdStyle}>{e.reference ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}
